import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { Menu, X } from 'lucide-react'
import * as React from 'react'
import { Group, Panel, Separator, useDefaultLayout } from 'react-resizable-panels'
import { cn } from '../lib/cn'
import { useKeyboardInset } from '../lib/keyboard-inset'
import { safeStorage } from '../lib/storage'
import { useIsSinglePane } from '../lib/use-media-query'

export type ShellPane = 'nav' | 'main' | 'rail'

export interface AppShellProps {
  topBar: React.ReactNode
  nav: React.ReactNode
  main: React.ReactNode
  /** Right pane (Riley + context). Omit for screens that use the full width (dispatch board, billing). */
  rail?: React.ReactNode
  railOpen?: boolean
  /** Riley full-size: the rail takes the centre too. The centre stays mounted (React <Activity>). */
  railFull?: boolean
  /** Banner under the top bar (API down, mirror stale, backfill progress). */
  banner?: React.ReactNode
  /** <900px: which single pane shows, and the bottom tab bar. */
  mobilePane?: ShellPane
  bottomTabs?: React.ReactNode
  /** Phone: the lane nav opens as a drawer over the current page. Riley stays a bottom tab. */
  navOpen?: boolean
  onNavOpenChange?: (open: boolean) => void
  /** Layout persistence id (pane sizes are a per-viewer convenience in localStorage). */
  layoutId?: string
}

function ResizeHandle() {
  return (
    <Separator className="group relative w-px bg-border outline-none transition-colors hover:bg-primary data-[separator=active]:bg-primary focus-visible:bg-primary">
      <span className="absolute inset-y-0 -left-1 -right-1" />
    </Separator>
  )
}

/**
 * Three resizable panes: lanes (left, ~260px) · list or case (centre) · Riley + context (right,
 * ~380px). Below 900px: one pane at a time with bottom tabs.
 */
export function AppShell({
  topBar,
  nav,
  main,
  rail,
  railOpen = true,
  railFull = false,
  banner,
  mobilePane = 'main',
  bottomTabs,
  navOpen = false,
  onNavOpenChange,
  layoutId = 'patchbay-shell',
}: AppShellProps) {
  const single = useIsSinglePane()
  useKeyboardInset(single)
  const showRail = Boolean(rail) && railOpen
  const mode = showRail ? (railFull ? 'full' : 'rail') : 'none'
  const panelIds = mode === 'none' ? ['nav', 'main'] : mode === 'full' ? ['nav', 'rail'] : ['nav', 'main', 'rail']
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: `${layoutId}:${mode}`,
    panelIds,
    storage: safeStorage,
  })

  if (single) {
    const pane = mobilePane === 'rail' && rail ? rail : mobilePane === 'nav' ? nav : main
    return (
      <div
        className="fixed inset-x-0 top-(--vv-offset-top,0px) flex h-(--app-height,100dvh) max-h-(--app-height,100dvh) w-full min-w-0 max-w-full flex-col overflow-x-clip bg-background"
        data-shell="single"
      >
        {topBar}
        {banner}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-clip overflow-y-auto">{pane}</main>
        {bottomTabs}
        <NavDrawer open={navOpen} onOpenChange={onNavOpenChange}>
          {nav}
        </NavDrawer>
      </div>
    )
  }

  return (
    <div className="flex h-dvh min-w-0 max-w-full flex-col overflow-x-clip bg-canvas" data-shell="panes" data-rail={mode}>
      {topBar}
      {banner}
      <Group key={mode} orientation="horizontal" className="min-h-0 flex-1" defaultLayout={defaultLayout} onLayoutChanged={onLayoutChanged}>
        <Panel id="nav" defaultSize="260px" minSize="240px" maxSize="360px" className="h-full overflow-y-auto bg-canvas">
          <nav aria-label="Lanes" className="h-full">
            {nav}
          </nav>
        </Panel>
        <ResizeHandle />
        {mode !== 'full' ? (
          <Panel id="main" minSize="30%" className="h-full overflow-hidden bg-background">
            <main className="flex h-full flex-col">{main}</main>
          </Panel>
        ) : null}
        {mode === 'rail' ? <ResizeHandle /> : null}
        {showRail ? (
          <Panel
            id="rail"
            defaultSize={railFull ? undefined : '380px'}
            minSize={railFull ? '50%' : '300px'}
            maxSize={railFull ? '100%' : '50%'}
            className="h-full overflow-hidden bg-canvas"
          >
            <aside aria-label="Riley" className="h-full">
              {rail}
            </aside>
          </Panel>
        ) : null}
      </Group>
      {/* Riley full-size hides the centre without unmounting it: drafts and scroll survive. */}
      {mode === 'full' ? (
        <React.Activity mode="hidden">
          <div hidden>{main}</div>
        </React.Activity>
      ) : null}
    </div>
  )
}

/** Lane nav over the current phone page. A link inside it closes the drawer. */
function NavDrawer({ open, onOpenChange, children }: { open: boolean; onOpenChange?: (open: boolean) => void; children: React.ReactNode }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop className="fixed inset-0 z-40 bg-foreground/25 transition-opacity duration-(--duration-base) data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <DialogPrimitive.Popup
          id="shell-nav-drawer"
          className="fixed inset-y-0 left-0 z-50 flex w-[min(20rem,100%)] max-w-full flex-col bg-canvas shadow-lg outline-none"
          onClick={(event) => {
            const target = event.target
            // Let a client-side router link finish its own click before unmounting the drawer. Closing it
            // synchronously here could cancel a real touch tap before the router committed the navigation.
            if (target instanceof Element && target.closest('a')) requestAnimationFrame(() => onOpenChange?.(false))
          }}
        >
          <div className="flex h-(--spacing-topbar) shrink-0 items-center justify-between gap-2 border-b border-border px-3">
            <DialogPrimitive.Title className="text-sm font-semibold">Navigation</DialogPrimitive.Title>
            <DialogPrimitive.Close
              data-slot="dialog-close"
              aria-label="Close navigation"
              className="inline-flex size-11 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
            >
              <X className="size-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export function ShellMenuButton({ open, onOpen }: { open?: boolean; onOpen: () => void }) {
  return (
    <button
      type="button"
      data-slot="shell-icon"
      aria-label="Open navigation"
      aria-expanded={open || undefined}
      aria-controls="shell-nav-drawer"
      onClick={onOpen}
      className="inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground hover:bg-muted"
    >
      <Menu className="size-5" />
    </button>
  )
}

export function BottomTabs({
  items,
}: {
  items: { id: string; label: string; icon: React.ReactNode; active?: boolean; onSelect: () => void; badge?: boolean }[]
}) {
  return (
    <nav
      aria-label="Sections"
      className="grid min-h-(--spacing-tabbar) shrink-0 border-t border-border bg-background pb-[env(safe-area-inset-bottom)]"
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
    >
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          data-slot="bottom-tab"
          onClick={it.onSelect}
          aria-current={it.active ? 'page' : undefined}
          className={cn(
            'relative flex h-full min-h-11 cursor-pointer flex-col items-center justify-center gap-0.5 text-xs [&_svg]:size-5',
            it.active ? 'text-primary' : 'text-muted-foreground',
          )}
        >
          {it.icon}
          {it.label}
          {it.active ? <span className="absolute inset-x-6 bottom-1 h-0.5 rounded-full bg-primary" /> : null}
        </button>
      ))}
    </nav>
  )
}
